import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import type { RootState } from "../rootReducer";

export type SyncTable =
  | "recipes"
  | "ingredients"
  | "methodSteps"
  | "recipeCollections"
  | "collections"
  | "recentViews"
  | "cookingProgress"
  | "cookingHistory"
  | "searchHistory"
  | "mealPlan"
  | "shoppingLists"
  | "shoppingListItems";

interface TableSync {
  dirtyIds: string[];
  lastPulledAt: string | null;
}
interface SyncState {
  byTable: Record<SyncTable, TableSync>;
  status: "idle" | "syncing" | "error";
  lastSyncAt: string | null;
  lastError: string | null;
  ownerUserId: string | null; // which user this persisted store belongs to
  realtimeConnected: boolean; // Realtime socket subscribed (ephemeral, not meaningful across restarts)
  isOnline: boolean; // device connectivity (ephemeral; corrected by NetInfo on mount)
  // Account-synced preferences are a per-user singleton (one row), not an entity
  // table, so they're tracked separately from `byTable`: a single dirty flag +
  // one pull cursor. See createSyncManager's push/pullPreferences.
  prefsDirty: boolean;
  prefsLastPulledAt: string | null;
  // Whether the preferences query has completed at least once this session/account
  // (regardless of whether a row existed). Until then we don't know the account's
  // real hasSeenOnboarding, so the onboarding gate holds off. Reset on user switch.
  prefsPulled: boolean;
}

const emptyTable = (): TableSync => ({ dirtyIds: [], lastPulledAt: null });

// Single source of truth for which slices participate in sync. The dirty-tracking
// middleware diffs exactly these slices, so this must match their reducer keys.
export const SYNC_TABLES: SyncTable[] = [
  "recipes",
  "ingredients",
  "methodSteps",
  "recipeCollections",
  "collections",
  "recentViews",
  "cookingProgress",
  "cookingHistory",
  "searchHistory",
  "mealPlan",
  "shoppingLists",
  "shoppingListItems",
];

const initialState: SyncState = {
  byTable: Object.fromEntries(
    SYNC_TABLES.map((t) => [t, emptyTable()]),
  ) as SyncState["byTable"],
  status: "idle",
  lastSyncAt: null,
  lastError: null,
  ownerUserId: null,
  realtimeConnected: false,
  isOnline: true, // assume online until NetInfo says otherwise
  prefsDirty: false,
  prefsLastPulledAt: null,
  prefsPulled: false,
};

// Self-healing accessor: persisted state written before a table was added to
// SYNC_TABLES won't have its byTable entry, so create it on demand. Keeps the
// slice robust against shape changes without a redux-persist migration.
function tableState(state: SyncState, table: SyncTable): TableSync {
  if (!state.byTable[table]) state.byTable[table] = emptyTable();
  return state.byTable[table];
}

const syncSlice = createSlice({
  name: "sync",
  initialState,
  reducers: {
    markDirty(state, action: PayloadAction<{ table: SyncTable; id: string }>) {
      const t = tableState(state, action.payload.table);
      if (!t.dirtyIds.includes(action.payload.id)) t.dirtyIds.push(action.payload.id);
    },
    // Batched dirty-marking: one dispatch for many rows (bulk add / clear), so a
    // 200-item change costs 1 dispatch + O(n) dedupe instead of 200 dispatches
    // each doing an O(n) includes() check.
    markManyDirty(state, action: PayloadAction<{ table: SyncTable; ids: string[] }>) {
      const t = tableState(state, action.payload.table);
      const seen = new Set(t.dirtyIds);
      for (const id of action.payload.ids) {
        if (!seen.has(id)) {
          seen.add(id);
          t.dirtyIds.push(id);
        }
      }
    },
    clearDirty(state, action: PayloadAction<{ table: SyncTable; ids: string[] }>) {
      const t = tableState(state, action.payload.table);
      const remove = new Set(action.payload.ids);
      t.dirtyIds = t.dirtyIds.filter((id) => !remove.has(id));
    },
    setLastPulledAt(state, action: PayloadAction<{ table: SyncTable; at: string }>) {
      tableState(state, action.payload.table).lastPulledAt = action.payload.at;
    },
    setSyncStatus(state, action: PayloadAction<SyncState["status"]>) {
      state.status = action.payload;
    },
    setSyncError(state, action: PayloadAction<string | null>) {
      state.lastError = action.payload;
    },
    setLastSyncAt(state, action: PayloadAction<string>) {
      state.lastSyncAt = action.payload;
    },
    setSyncOwner(state, action: PayloadAction<string | null>) {
      state.ownerUserId = action.payload;
    },
    setRealtimeConnected(state, action: PayloadAction<boolean>) {
      state.realtimeConnected = action.payload;
    },
    setOnline(state, action: PayloadAction<boolean>) {
      state.isOnline = action.payload;
    },
    // Account-synced preferences (singleton) dirty flag + pull cursor.
    markPrefsDirty(state) {
      state.prefsDirty = true;
    },
    clearPrefsDirty(state) {
      state.prefsDirty = false;
    },
    setPrefsLastPulledAt(state, action: PayloadAction<string>) {
      state.prefsLastPulledAt = action.payload;
    },
    setPrefsPulled(state, action: PayloadAction<boolean>) {
      state.prefsPulled = action.payload;
    },
    resetSync: () => initialState, // call on logout / user switch
  },
});

export const {
  markDirty,
  markManyDirty,
  clearDirty,
  setLastPulledAt,
  setSyncStatus,
  setSyncError,
  setLastSyncAt,
  setSyncOwner,
  setRealtimeConnected,
  setOnline,
  markPrefsDirty,
  clearPrefsDirty,
  setPrefsLastPulledAt,
  setPrefsPulled,
  resetSync,
} = syncSlice.actions;

// Whether the account's preferences have been pulled at least once — gates the
// onboarding decision (see OnboardingGate) so it reads the real synced value.
export const selectPrefsPulled = (s: RootState): boolean => s.sync.prefsPulled ?? false;

export default syncSlice.reducer;
